import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import PinPad from './PinPad';
import BiometricPrompt from './BiometricPrompt';
import { useAuth } from '@/hooks/useAuth';
import { useBiometric } from '@/hooks/useBiometric';
import { Sparkles } from 'lucide-react';
import { initHaptics, lightImpact } from '@/utils/haptics';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading, error, clearError } = useAuth();
  const { isAvailable, isEnabled, promptBiometric, enableBiometric } = useBiometric();
  const [showBiometricPrompt, setShowBiometricPrompt] = useState(false);
  const [showPin, setShowPin] = useState(false);

  useEffect(() => {
    initHaptics();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const tryBiometric = async () => {
      if (isEnabled && isAvailable) {
        const success = await promptBiometric();
        setShowPin(true);
        if (success) {
          // Biometric verified - still need PIN for backend
        }
      } else {
        setShowPin(true);
      }
    };
    if (!isAuthenticated) {
      tryBiometric();
    }
  }, [isEnabled, isAvailable, isAuthenticated, promptBiometric]);

  const handlePinSubmit = useCallback(
    async (pin: string) => {
      clearError();
      const success = await login(pin);
      if (success && isAvailable) {
        setShowBiometricPrompt(true);
      }
    },
    [login, clearError, isAvailable]
  );

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-950 px-6 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="flex flex-col items-center mb-10 relative z-10"
      >
        <motion.div
          animate={{ rotate: [0, 5, -5, 0] }}
          transition={{ repeat: Infinity, duration: 4, ease: 'easeInOut' }}
          className="w-24 h-24 rounded-3xl gradient-bg flex items-center justify-center mb-6 shadow-glow-lg"
        >
          <Sparkles className="w-12 h-12 text-white" />
        </motion.div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2 tracking-tight">
          Sheehan AI
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 text-center font-medium">
          Family intelligence, personalized.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="w-full max-w-sm glass-strong rounded-3xl p-8 shadow-elevated relative z-10"
      >
        <p className="text-center text-sm font-medium text-slate-600 dark:text-slate-300 mb-6">
          Enter your 4-digit PIN
        </p>

        {showPin ? (
          <PinPad onSubmit={handlePinSubmit} disabled={isLoading} error={error} />
        ) : (
          <div className="flex items-center justify-center py-12">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
              className="w-8 h-8 border-3 border-primary-600 border-t-transparent rounded-full"
              style={{ borderWidth: '3px' }}
            />
          </div>
        )}

        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={async () => { await lightImpact(); window.location.href = 'mailto:tommy@sheehanai.com'; }}
          className="w-full mt-6 text-center text-xs text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
        >
          Forgot PIN? Contact Tommy
        </motion.button>
      </motion.div>

      <BiometricPrompt
        isOpen={showBiometricPrompt}
        onEnable={enableBiometric}
        onSkip={() => setShowBiometricPrompt(false)}
        isAvailable={isAvailable}
      />
    </div>
  );
};

export default LoginPage;
